import { Suspense, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FiAlertCircle, FiChevronDown, FiDownload, FiHome, FiInbox, FiLogOut, FiMessageCircle, FiPlusCircle, FiShield, FiTag, FiUploadCloud, FiUser, FiUsers } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useOrders } from "../hooks/useOrders";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useMyPlan } from "../hooks/useMyPlan";
import { inGrace } from "../lib/billing";
import { canOpen, pendingCount, useNow } from "../lib/expiry";
import { formatDate } from "../lib/format";
import { usePWAInstall } from "../hooks/usePWAInstall";
import { ROLES } from "../lib/constants";
import { fullName } from "../lib/format";
import { orderTitle } from "../lib/orders";
import { shortCode, sortByUnread, totalUnread, unreadCount } from "../lib/unread";
import LegalFooter from "./LegalFooter";
import Logo from "./Logo";
import { Avatar, PageLoader, cx } from "./ui";

const NAV = [
  { to: "/dashboard", label: "Inicio", icon: FiHome },
  { to: "/nueva-orden", label: "Nueva orden", icon: FiPlusCircle },
  { to: "/enviar", label: "Enviar archivos", shortLabel: "Enviar", icon: FiUploadCloud },
  { to: "/historial", label: "Historial", icon: FiInbox },
];

function UserMenu() {
  const { profile, logout } = useAuth();
  const { isInstallable, installApp } = usePWAInstall();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const name = fullName(profile);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => !menuRef.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const itemClass = "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100";

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 rounded-full p-1 pr-2 transition hover:bg-slate-100"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={name} size="sm" />
        <span className="hidden max-w-[10rem] truncate text-sm font-semibold text-slate-700 sm:block">{name}</span>
        <FiChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-64 animate-fade-in rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
          <div className="px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-slate-900">{name}</p>
            <p className="truncate text-xs text-slate-500">{profile?.email}</p>
            <p className="mt-1 text-xs font-medium text-brand-700">{ROLES[profile?.rol]?.label}</p>
          </div>
          <div className="my-1 border-t border-slate-100" />
          <Link to="/account" className={itemClass} role="menuitem">
            <FiUser className="h-4 w-4 text-slate-400" /> Mi cuenta
          </Link>
          <Link to="/planes" className={itemClass} role="menuitem">
            <FiTag className="h-4 w-4 text-slate-400" /> Planes
          </Link>
          <Link to="/contactos" className={itemClass} role="menuitem">
            <FiUsers className="h-4 w-4 text-slate-400" /> Contactos
          </Link>
          {isAdmin && (
            <Link to="/admin" className={itemClass} role="menuitem">
              <FiShield className="h-4 w-4 text-slate-400" /> Administración
            </Link>
          )}
          {isInstallable && (
            <button onClick={installApp} className={itemClass} role="menuitem">
              <FiDownload className="h-4 w-4 text-slate-400" /> Instalar la app
            </button>
          )}
          <button onClick={handleLogout} className={cx(itemClass, "text-rose-600 hover:bg-rose-50")} role="menuitem">
            <FiLogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

// Órdenes recibidas que todavía nadie empezó a trabajar (sin las vencidas para mí ni las borradas: no se pueden abrir).
function usePendientes() {
  const { profile } = useAuth();
  const { orders } = useOrders();
  return pendingCount(orders, profile.uid, useMyPlan(), useNow());
}

// Mensajes sin leer de todas mis órdenes. Sale de la misma suscripción a las órdenes: no suma lecturas.
// Tampoco cuentan las órdenes vencidas para mí ni las borradas (no se pueden abrir).
function useNoLeidos() {
  const { profile } = useAuth();
  const { orders: todas } = useOrders();
  const plan = useMyPlan();
  const now = useNow();
  const orders = todas.filter((o) => canOpen(o, profile.uid, plan, now));
  const total = totalUnread(orders, profile.uid);
  const conMensajes = sortByUnread(orders, profile.uid).filter((o) => unreadCount(o, profile.uid) > 0);

  useEffect(() => {
    document.title = total > 0 ? `(${total}) OdontoSend` : "OdontoSend";
  }, [total]);

  return { total, conMensajes };
}

function UnreadMenu() {
  const { profile } = useAuth();
  const { total, conMensajes } = useNoLeidos();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => !menuRef.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={total > 0 ? `${total} mensaje${total !== 1 ? "s" : ""} sin leer` : "Mensajes"}
      >
        <FiMessageCircle className="h-5 w-5" />
        <CountBadge count={total} className="absolute -right-0.5 -top-0.5" />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] animate-fade-in rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
          <p className="px-3 py-2 text-sm font-semibold text-slate-900">Mensajes sin leer</p>
          {conMensajes.length === 0 ? (
            <p className="px-3 pb-3 text-sm text-slate-500">Estás al día. No tenés mensajes nuevos.</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {conMensajes.map((order) => (
                <li key={order.id}>
                  <Link to={`/ordenes/${order.id}`} role="menuitem" className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {orderTitle(order)} <span className="font-normal text-slate-400">{shortCode(order.id)}</span>
                      </p>
                      <p className="truncate text-xs text-slate-500">
                        {order.senderUid === profile.uid ? order.recipientNombre || order.recipientEmail : order.senderNombre || order.senderEmail}
                      </p>
                    </div>
                    <CountBadge count={unreadCount(order, profile.uid)} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function CountBadge({ count, className }) {
  if (!count) return null;
  return (
    <span className={cx("flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white", className)}>
      {count > 99 ? "99+" : count}
    </span>
  );
}

// Un cobro falló y corren los 5 días de gracia: aviso en toda la app.
function GraceBanner() {
  const plan = useMyPlan();
  const s = plan?.suscripcion;
  if (!inGrace(s)) return null;
  return (
    <div role="alert" className="border-b border-rose-200 bg-rose-50 print:hidden">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm text-rose-800 sm:px-6">
        <FiAlertCircle className="h-4 w-4 shrink-0" />
        <span>
          No pudimos cobrar tu plan. Mercado Pago lo va a reintentar; lo mantenés hasta el {formatDate(s.graciaHasta)}.
        </span>
        <Link to="/account" className="font-semibold underline">
          Ver mi cuenta
        </Link>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const pendientes = usePendientes();
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/85 backdrop-blur-md print:hidden">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-8 px-4 sm:px-6">
          <Link to="/dashboard" className="shrink-0">
            <Logo className="h-8 sm:h-9" />
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cx(
                    "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition",
                    isActive ? "bg-brand-50 text-brand-800" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
                {to === "/historial" && <CountBadge count={pendientes} />}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <UnreadMenu />
            <UserMenu />
          </div>
        </div>
      </header>

      <GraceBanner />

      <main className="flex-1 pb-8">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>

      {/* En celulares, por encima de la navegación inferior. */}
      <LegalFooter className="mb-[calc(4.25rem+env(safe-area-inset-bottom))] md:mb-0" />

      {/* Navegación inferior en celulares */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden print:hidden">
        <div className="grid grid-cols-4">
          {NAV.map(({ to, label, shortLabel, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cx(
                  "flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition",
                  isActive ? "text-brand-700" : "text-slate-500"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className={cx("relative flex h-7 w-12 items-center justify-center rounded-full transition", isActive && "bg-brand-50")}>
                    <Icon className="h-5 w-5" />
                    {to === "/historial" && <CountBadge count={pendientes} className="absolute -top-1 right-1.5" />}
                  </span>
                  {shortLabel || label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
